import type { CanvasGroupGeometry } from './types';

// ── JSON Spec ─────────────────────────────────────────────────────────

export interface CanvasGroupSpec {
  groupName: string;
  canvas: {
    widthPx: number;
    heightPx: number;
    widthMm: number;
    heightMm: number;
    aspectRatio: string;
    density: number;
  };
  monitors: {
    index: number;
    name: string;
    stationId: string | null;
    cropPx: { x: number; y: number; w: number; h: number };
    nativeResolution: { w: number; h: number };
    physicalMm: { w: number; h: number };
    bezelMm: { top: number; right: number; bottom: number; left: number };
  }[];
  deadZones: {
    x: number;
    w: number;
    gapMm: number;
    between: [string, string];
  }[];
  prompt: string;
}

function gcd(a: number, b: number): number {
  return b === 0 ? a : gcd(b, a % b);
}

function formatAspect(w: number, h: number): string {
  const d = gcd(w, h);
  return `${w / d}:${h / d}`;
}

export function buildSpec(groupName: string, geo: CanvasGroupGeometry): CanvasGroupSpec {
  const monitors = geo.slices.map((s, i) => ({
    index: i + 1,
    name: s.name,
    stationId: s.monitorRef.stationId,
    cropPx: { x: s.x, y: s.y, w: s.w, h: s.h },
    nativeResolution: { w: s.nativeW, h: s.nativeH },
    physicalMm: { w: s.physW, h: s.physH },
    bezelMm: { top: s.bezelTop, right: s.bezelRight, bottom: s.bezelBottom, left: s.bezelLeft },
  }));

  const deadZones = geo.deadZones.map((dz) => ({
    x: dz.x, w: dz.w, gapMm: dz.gapMm,
    between: [dz.leftMonitor, dz.rightMonitor] as [string, string],
  }));

  const aspect = formatAspect(geo.renderWidth, geo.renderHeight);

  // Build a ready-to-use prompt snippet
  const monitorList = monitors.map((m) =>
    `  #${m.index} "${m.name}": ${m.nativeResolution.w}x${m.nativeResolution.h}, ${m.physicalMm.w}x${m.physicalMm.h}mm`
  ).join('\n');

  const dzList = deadZones.length > 0
    ? '\n\nDead zones (bezel gaps — content here is hidden behind bezels):\n' +
      deadZones.map((dz) => `  ${dz.gapMm}mm gap between "${dz.between[0]}" and "${dz.between[1]}" at x=${dz.x}..${dz.x + dz.w}px`).join('\n')
    : '';

  const prompt = [
    `Generate a seamless video at ${geo.renderWidth}x${geo.renderHeight} pixels (aspect ratio ${aspect}).`,
    `This video will be displayed across ${monitors.length} physical monitor${monitors.length > 1 ? 's' : ''} arranged side by side.`,
    `Total physical size: ${(geo.totalWidthMm / 1000).toFixed(2)}m wide x ${(geo.maxHeightMm / 1000).toFixed(2)}m tall.`,
    `\nMonitors (left to right):\n${monitorList}`,
    dzList,
    `\nIMPORTANT: Avoid placing faces, text, or focal points in the dead zones (bezel gap regions).`,
    `The content should flow naturally across all monitors as one continuous scene.`,
  ].join('\n');

  return {
    groupName,
    canvas: {
      widthPx: geo.renderWidth,
      heightPx: geo.renderHeight,
      widthMm: Math.round(geo.totalWidthMm),
      heightMm: Math.round(geo.maxHeightMm),
      aspectRatio: aspect,
      density: Math.round(geo.density * 100) / 100,
    },
    monitors,
    deadZones,
    prompt,
  };
}

// ── Guide PNG ─────────────────────────────────────────────────────────

export function renderGuideImage(geo: CanvasGroupGeometry): HTMLCanvasElement {
  // Scale to fit within 2400px wide for a clear guide
  const maxW = 2400;
  const scale = Math.min(1, maxW / geo.renderWidth);
  const cw = Math.round(geo.renderWidth * scale);
  const ch = Math.round(geo.renderHeight * scale) + 80; // extra space for labels below

  const canvas = document.createElement('canvas');
  canvas.width = cw;
  canvas.height = ch;
  const ctx = canvas.getContext('2d')!;

  // Background
  ctx.fillStyle = '#0f172a';
  ctx.fillRect(0, 0, cw, ch);

  // Draw dead zones first (red)
  for (const dz of geo.deadZones) {
    const x = Math.round(dz.x * scale);
    const w = Math.max(2, Math.round(dz.w * scale));
    ctx.fillStyle = 'rgba(239, 68, 68, 0.4)';
    ctx.fillRect(x, 0, w, Math.round(geo.renderHeight * scale));

    // Red dashed border
    ctx.strokeStyle = '#ef4444';
    ctx.lineWidth = 2;
    ctx.setLineDash([6, 4]);
    ctx.strokeRect(x, 0, w, Math.round(geo.renderHeight * scale));
    ctx.setLineDash([]);

    // Gap label
    ctx.fillStyle = '#ef4444';
    ctx.font = 'bold 11px monospace';
    ctx.textAlign = 'center';
    ctx.fillText(`${dz.gapMm}mm`, x + w / 2, Math.round(geo.renderHeight * scale) / 2);
    ctx.fillText('GAP', x + w / 2, Math.round(geo.renderHeight * scale) / 2 + 14);
  }

  // Draw monitor slices
  for (let i = 0; i < geo.slices.length; i++) {
    const s = geo.slices[i];
    const x = Math.round(s.x * scale);
    const y = Math.round(s.y * scale);
    const w = Math.round(s.w * scale);
    const h = Math.round(s.h * scale);

    // Visible area
    ctx.fillStyle = 'rgba(34, 211, 238, 0.08)';
    ctx.fillRect(x, y, w, h);

    // Border
    ctx.strokeStyle = '#22d3ee';
    ctx.lineWidth = 2;
    ctx.strokeRect(x, y, w, h);

    // Monitor number (large, centered)
    ctx.fillStyle = 'rgba(34, 211, 238, 0.3)';
    ctx.font = `bold ${Math.min(120, h * 0.4)}px monospace`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(`${i + 1}`, x + w / 2, y + h / 2);

    // Monitor name (top-left)
    ctx.fillStyle = '#22d3ee';
    ctx.font = 'bold 14px monospace';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'top';
    ctx.fillText(s.name, x + 8, y + 8);

    // Resolution (top-right)
    ctx.fillStyle = '#94a3b8';
    ctx.font = '12px monospace';
    ctx.textAlign = 'right';
    ctx.fillText(`${s.nativeW}x${s.nativeH}`, x + w - 8, y + 8);

    // Physical size (bottom-left)
    ctx.textAlign = 'left';
    ctx.fillText(`${s.physW}x${s.physH}mm`, x + 8, y + h - 22);

    // Crop coords (bottom-right)
    ctx.textAlign = 'right';
    ctx.fillStyle = '#64748b';
    ctx.fillText(`crop: ${s.x},${s.y} ${s.w}x${s.h}`, x + w - 8, y + h - 22);

    // Bottom label strip
    const labelY = Math.round(geo.renderHeight * scale) + 10;
    ctx.fillStyle = '#e2e8f0';
    ctx.font = 'bold 13px monospace';
    ctx.textAlign = 'center';
    ctx.fillText(`#${i + 1} ${s.name}`, x + w / 2, labelY + 10);
    ctx.fillStyle = '#94a3b8';
    ctx.font = '11px monospace';
    ctx.fillText(`${s.nativeW}x${s.nativeH} • ${s.physW}x${s.physH}mm`, x + w / 2, labelY + 28);
    if (s.monitorRef.stationId) {
      ctx.fillStyle = '#64748b';
      ctx.font = '10px monospace';
      ctx.fillText(s.monitorRef.stationId, x + w / 2, labelY + 44);
    }
  }

  // Title bar at top
  ctx.fillStyle = '#e2e8f0';
  ctx.font = 'bold 16px monospace';
  ctx.textAlign = 'left';
  ctx.textBaseline = 'top';

  // Canvas resolution label (top-left, over the image)
  const totalLabel = `${geo.renderWidth} x ${geo.renderHeight} px • ${(geo.totalWidthMm / 1000).toFixed(2)}m x ${(geo.maxHeightMm / 1000).toFixed(2)}m`;
  ctx.fillStyle = 'rgba(15, 23, 42, 0.8)';
  const metrics = ctx.measureText(totalLabel);
  ctx.fillRect(0, 0, metrics.width + 20, 28);
  ctx.fillStyle = '#f8fafc';
  ctx.font = 'bold 14px monospace';
  ctx.fillText(totalLabel, 10, 8);

  return canvas;
}

// ── Download helpers ──────────────────────────────────────────────────

export function downloadJson(spec: CanvasGroupSpec) {
  const blob = new Blob([JSON.stringify(spec, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${spec.groupName.replace(/\s+/g, '-').toLowerCase()}-spec.json`;
  a.click();
  URL.revokeObjectURL(url);
}

export function downloadGuideImage(canvas: HTMLCanvasElement, name: string) {
  canvas.toBlob((blob) => {
    if (!blob) return;
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${name.replace(/\s+/g, '-').toLowerCase()}-guide.png`;
    a.click();
    URL.revokeObjectURL(url);
  }, 'image/png');
}

export function copyPromptToClipboard(spec: CanvasGroupSpec): Promise<void> {
  return navigator.clipboard.writeText(spec.prompt);
}
